import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGroup } from '../../contexts/GroupContext';
import { useToast } from '../../components/common';
import DeveloperMark from '../../components/dev/DeveloperMark';
import api from '../../services/api';
import { copyText } from '../../utils/clipboard';
import { STEPS, writeOnboarding } from '../../utils/onboarding';
import { newBuildHandoff, useRemoteFlag } from '../create/handoff';
import useArrival from '../create/useArrival';
import './Welcome.css';

/**
 * /welcome — first run, one question and one button per screen.
 *
 * Somebody signing in to an empty space used to land on Home: a pipeline of
 * five empty columns, four figures reading zero and a "Start something" grid,
 * which is the portal explaining itself to a person with nothing in it yet.
 * This asks what they are building, mints a token, hands them the paste and
 * waits for the app to arrive — then opens it, in the full portal.
 *
 * NO FRAME. The rail is every door in the portal, and on the first visit every
 * one of them opens onto an empty page. The one way out is Skip, a real
 * button under every step rather than a faint link in a corner: leaving is a
 * choice the page offers, not one it hides.
 *
 * THE STEP AND KIND ARE IN THE ADDRESS so refresh and Back work. The TOKEN IS
 * NOT: it is shown once and held in memory only, so step 3 after a refresh is
 * the placeholder version of the same paste.
 */

const COPY_FAILED = 'Could not copy. Select the text and copy it by hand.';

const KINDS = [
  ['app', 'An app', 'People grant it access once, then it reads and writes their data.'],
  ['plugin', 'A plugin', 'Tools agents run on someone\'s data, each asking only for what it needs.'],
];

const stepOf = (raw) => {
  const n = Number.parseInt(raw, 10);
  return n >= 1 && n <= STEPS ? n : 1;
};

const initials = (name) => (name || '?').trim().slice(0, 2).toUpperCase();

function Screen({ step, children }) {
  return (
    <>
      <div className="welcome__step">Step {step} of {STEPS}</div>
      {children}
    </>
  );
}

function PickKind({ name, kind, onContinue }) {
  const [choice, setChoice] = useState(kind);
  return (
    <Screen step={1}>
      <h1 className="welcome__q">{name ? `Welcome, ${name}.` : 'Welcome.'} What are you building?</h1>
      <p className="welcome__p">Your coding assistant does the building. This takes about ten minutes.</p>
      <div className="welcome__choices" role="group" aria-label="What are you building">
        {KINDS.map(([id, label, sub]) => (
          <button
            key={id}
            type="button"
            className="welcome__choice"
            aria-pressed={choice === id}
            onClick={() => setChoice(id)}
          >
            <b>{label}</b>
            <span>{sub}</span>
          </button>
        ))}
      </div>
      <button type="button" className="welcome__btn" onClick={() => onContinue(choice)}>Continue</button>
    </Screen>
  );
}

function MintToken({ groupId, onToken, onHaveOne }) {
  const toast = useToast();
  const [machine, setMachine] = useState('');
  const [minting, setMinting] = useState(false);

  // The same mint as New app (CreateFlow): 90 days, for the active space.
  const mint = async (e) => {
    e.preventDefault();
    const label = machine.trim();
    if (!label || minting) return;
    setMinting(true);
    try {
      const res = await api.createDeveloperToken(label, 90, groupId || null);
      const value = res.token || res.developer_token || null;
      if (!value) toast.error('Token created, but the value was not returned. Check the Tokens list.');
      setMinting(false);
      onToken(value);
    } catch (err) {
      toast.error(err.message || 'Could not create a token.');
      setMinting(false);
    }
  };

  return (
    <Screen step={2}>
      <h1 className="welcome__q">Which machine are you on?</h1>
      <p className="welcome__p">
        We make a token so the CLI on this machine can act as you. It never touches your
        app&apos;s users&apos; data.
      </p>
      <form onSubmit={mint}>
        <input
          className="welcome__input"
          aria-label="Which machine?"
          placeholder="MacBook"
          value={machine}
          maxLength={100}
          autoFocus
          onChange={(e) => setMachine(e.target.value)}
        />
        <div className="welcome__btns">
          <button type="submit" className="welcome__btn" disabled={minting || !machine.trim()}>
            {minting ? 'Creating…' : 'Create token'}
          </button>
          <button type="button" className="welcome__ghost" onClick={onHaveOne}>I already have one</button>
        </div>
      </form>
    </Screen>
  );
}

function HandOver({ kind, token, groupId, groupName, onCopied, onMintOne }) {
  const toast = useToast();
  const remote = useRemoteFlag();
  const [failed, setFailed] = useState(false);
  const paste = newBuildHandoff({ kind, token, groupId, groupName, remote });

  const copy = async () => {
    if (await copyText(paste)) {
      setFailed(false);
      toast.success('Copied.');
      onCopied();
    } else {
      setFailed(true);
    }
  };

  return (
    <Screen step={3}>
      <h1 className="welcome__q">Paste this into your assistant</h1>
      <p className="welcome__p">
        Open an empty folder in Claude Code or Cursor and paste. It sets up the CLI, logs in
        and designs the {kind} with you.
      </p>
      <pre className="welcome__paste" data-testid="welcome-paste" tabIndex={0}>{paste}</pre>
      <div className="welcome__btns">
        <button type="button" className="welcome__btn" onClick={copy}>Copy the paste</button>
        {/* Always there: a refused clipboard, or someone who selected it by
            hand, must still be able to reach the waiting screen. */}
        <button type="button" className="welcome__ghost" onClick={onCopied}>I've pasted it</button>
      </div>
      {token ? (
        <p className="welcome__note">Your token is in it. Not shown again.</p>
      ) : (
        // No token on this page: a refresh, a resume from Home, or "I already
        // have one". Only the last has one to hand, so the way to make one stays.
        <p className="welcome__note">
          Put your token where it says &lt;YOUR_TOKEN&gt;. Don't have it?{' '}
          <button type="button" className="welcome__ghost welcome__ghost--inline" onClick={onMintOne}>Create a token</button>
        </p>
      )}
      {failed && <p className="welcome__err" role="alert">{COPY_FAILED}</p>}
    </Screen>
  );
}

function WaitForIt({ kind, groupId, onLanded, onOpen }) {
  const arrived = useArrival(groupId, onLanded);
  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  /* A SHORT BEAT, THEN THE APP. Jumping the instant it lands would read as the
     page breaking; the line saying so is what tells them it worked. */
  useEffect(() => {
    if (!arrived) return undefined;
    const t = setTimeout(() => openRef.current(arrived), 1200);
    return () => clearTimeout(t);
  }, [arrived]);

  return (
    <Screen step={4}>
      <h1 className="welcome__q">Your {kind} appears here when it registers</h1>
      <p className="welcome__p">
        Leave this open. Your assistant registers it with{' '}
        <code>{kind === 'plugin' ? 'paradigm plugin push' : 'paradigm app push'}</code>, usually
        after you have agreed the design together.
      </p>
      {arrived ? (
        <div className="welcome__arrived" role="status">
          <span className="welcome__face" aria-hidden="true">{initials(arrived.name)}</span>
          <div><b>{arrived.name} is registered</b><span>Opening it…</span></div>
        </div>
      ) : (
        <div className="welcome__wait"><span className="welcome__pulse" aria-hidden="true" />Waiting for your {kind}…</div>
      )}
    </Screen>
  );
}

export default function Welcome() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { activeGroupId, activeGroup } = useGroup();
  const userId = user?.id;
  const step = stepOf(params.get('step'));
  const kind = params.get('kind') === 'plugin' ? 'plugin' : 'app';
  const [token, setToken] = useState(null);       // memory only, never stored

  const go = (next, nextKind = kind) => setParams({ step: String(next), kind: nextKind });

  // Where they got to, so Home's "Finish setting up" reopens it here.
  useEffect(() => {
    writeOnboarding(userId, activeGroupId, { step, kind });
  }, [userId, activeGroupId, step, kind]);

  const skip = () => {
    writeOnboarding(userId, activeGroupId, { state: 'skipped', step, kind });
    navigate('/home');
  };

  // Done the moment it lands, not when the page moves: closing the tab during
  // the beat must not bring them back here.
  const landed = () => writeOnboarding(userId, activeGroupId, { state: 'done' });
  const open = (app) => navigate(app.is_plugin ? `/plugins/${app.id}` : `/applications/${app.id}`, { replace: true });

  const first = (user?.first_name || '').trim().split(/\s+/)[0] || user?.username || '';

  return (
    <div className="welcome">
      <header className="welcome__top">
        <DeveloperMark size={26} title="ofSelf Developer" />
        <span
          className="welcome__prog"
          role="progressbar"
          aria-label={`Step ${step} of ${STEPS}`}
          aria-valuemin={1}
          aria-valuemax={STEPS}
          aria-valuenow={step}
        >
          {Array.from({ length: STEPS }, (_, i) => <i key={i} data-on={i < step || undefined} />)}
        </span>
        <span aria-hidden="true" />
      </header>

      <main className="welcome__mid">
        {step === 1 && <PickKind key={kind} name={first} kind={kind} onContinue={(k) => go(2, k)} />}
        {step === 2 && (
          <MintToken
            groupId={activeGroupId}
            onToken={(value) => { setToken(value); go(3); }}
            onHaveOne={() => go(3)}
          />
        )}
        {step === 3 && (
          <HandOver
            kind={kind}
            token={token}
            groupId={activeGroupId}
            groupName={activeGroup?.name}
            onCopied={() => go(4)}
            onMintOne={() => go(2)}
          />
        )}
        {step === 4 && <WaitForIt kind={kind} groupId={activeGroupId} onLanded={landed} onOpen={open} />}

        <div className="welcome__out">
          <button type="button" className="welcome__skip" onClick={skip}>
            {step === STEPS ? 'Go to the portal' : 'Skip for now'}
          </button>
          <span className="welcome__out-note">
            {step === STEPS
              ? `Your ${kind} shows up on Home when it registers.`
              : 'Go straight to the portal. You can finish this from Home any time.'}
          </span>
        </div>
      </main>
    </div>
  );
}
